/**
 * Utilities for Skills Builder - Validation, formatting, and helpers
 *
 * Pure functions for client-side validation and display formatting.
 * API functions live in api/skills.api.ts.
 */

import type { Skill, SkillFrontmatter, SkillSummary } from '@/skillsBuilder/skillsBuilder.types';

// ============================================================================
// SKILL.md Validation (Client-side)
// ============================================================================

/**
 * Parse SKILL.md frontmatter (client-side)
 * Returns null if parsing fails
 */
export const parseSkillMdFrontmatter = (content: string): SkillFrontmatter | null => {
  const frontmatterMatch = content.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!frontmatterMatch) {
    return null;
  }

  const yamlContent = frontmatterMatch[1];
  const lines = yamlContent.split('\n');
  const result: Record<string, string> = {};

  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      result[key] = value;
    }
  }

  if (!result.name || !result.description) {
    return null;
  }

  return {
    name: result.name,
    description: result.description,
    license: result.license,
    compatibility: result.compatibility,
  };
};

/**
 * Validate skill name (Agent Skills specification)
 */
export const isValidSkillName = (name: string): boolean => {
  if (!name || name.length === 0) return false;
  if (name.length > 64) return false;
  // Must be lowercase letters, numbers, and hyphens only
  // Must not start or end with hyphen
  // No consecutive hyphens
  const nameRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  if (!nameRegex.test(name)) return false;
  // Cannot contain reserved words
  const reserved = ['anthropic', 'claude'];
  for (const word of reserved) {
    if (name.includes(word)) return false;
  }
  return true;
};

/**
 * Validate skill description (Agent Skills specification)
 */
export const isValidSkillDescription = (description: string): boolean => {
  if (!description || description.trim().length === 0) return false;
  if (description.length > 1024) return false;
  return true;
};

/**
 * Validate SKILL.md content
 * Returns array of error messages (empty if valid)
 */
export const validateSkillMd = (content: string): string[] => {
  const errors: string[] = [];

  if (!content || content.trim().length === 0) {
    errors.push('SKILL.md content is empty');
    return errors;
  }

  // Check for frontmatter
  if (!content.startsWith('---')) {
    errors.push('SKILL.md must start with YAML frontmatter (--- delimiter)');
    return errors;
  }

  const frontmatter = parseSkillMdFrontmatter(content);
  if (!frontmatter) {
    errors.push('Invalid YAML frontmatter or missing required fields (name, description)');
    return errors;
  }

  // Validate name
  if (!isValidSkillName(frontmatter.name)) {
    errors.push(
      'Invalid skill name: must be 1-64 lowercase letters, numbers, and hyphens; cannot start/end with hyphen or have consecutive hyphens',
    );
  }

  // Validate description
  if (!isValidSkillDescription(frontmatter.description)) {
    errors.push('Invalid description: must be 1-1024 characters and non-empty');
  }

  return errors;
};

// ============================================================================
// Python/Script Validation
// ============================================================================

const PYTHON_KEYWORDS = [
  'False',
  'None',
  'True',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'while',
  'with',
  'yield',
] as const;

/**
 * Check if a name is a valid Python identifier
 */
export const isValidPythonIdentifier = (name: string): boolean => {
  if (!name || name.length === 0) return false;
  // Must start with letter or underscore, followed by letters, digits, or underscores
  const identifierRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  if (!identifierRegex.test(name)) return false;
  return !PYTHON_KEYWORDS.includes(name as (typeof PYTHON_KEYWORDS)[number]);
};

/**
 * Validate a script filename
 */
export const isValidScriptFilename = (filename: string): boolean => {
  if (!filename || filename.length === 0) return false;
  if (!filename.endsWith('.py')) return false;
  // Get name without extension
  const name = filename.slice(0, -3);
  return isValidPythonIdentifier(name);
};

/**
 * Basic Python syntax check (very simple, just checks for common issues)
 */
export const checkPythonSyntax = (code: string): string[] => {
  const errors: string[] = [];

  // Check for unmatched quotes
  const tripleQuotes = (code.match(/"""/g) || []).length;
  const tripleSingleQuotes = (code.match(/'''/g) || []).length;

  // Triple quotes come in pairs
  if (tripleQuotes % 2 !== 0) {
    errors.push('Unmatched triple double quotes (""")');
  }
  if (tripleSingleQuotes % 2 !== 0) {
    errors.push("Unmatched triple single quotes (''')");
  }

  return errors;
};

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a date string for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format a date string as relative time
 */
export const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return formatDate(dateString);
};

// ============================================================================
// Conversion Utilities
// ============================================================================

/**
 * Convert a Python module name to a valid skill name
 */
export const moduleNameToSkillName = (moduleName: string): string => moduleName.toLowerCase().replace(/_/g, '-');

/**
 * Convert a skill name to a valid Python module name
 */
export const skillNameToModuleName = (skillName: string): string => skillName.replace(/-/g, '_');

// ============================================================================
// API error handling
// ============================================================================

type ApiErrorShape = {
  message?: unknown;
  data?: unknown;
  response?: { data?: unknown };
};

const messageFromResponseData = (data: unknown): string | null => {
  if (typeof data === 'string' && data.trim()) return data;
  if (!data || typeof data !== 'object') return null;
  const { detail, statusMessage } = data as { detail?: unknown; statusMessage?: unknown };
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors (422) put a list of {msg, loc, ...} entries in detail
    const messages = detail
      .map((entry) =>
        entry && typeof entry === 'object' && typeof (entry as { msg?: unknown }).msg === 'string'
          ? (entry as { msg: string }).msg
          : null,
      )
      .filter((msg): msg is string => msg !== null);
    if (messages.length > 0) return messages.join('; ');
  }
  if (typeof statusMessage === 'string' && statusMessage.trim()) return statusMessage;
  return null;
};

/**
 * Extract a human-readable message from a failed skills API call.
 *
 * The global axios error interceptor rejects with a plain object ({...error, ...error.response})
 * rather than an Error, so `instanceof Error` checks fail, the non-enumerable `message` is lost
 * to the spread, and String(err) renders "[object Object]". The actionable message from the
 * GenAI service lives in the response body's `detail` field (FastAPI convention); appserver
 * responses use `statusMessage`.
 */
export const extractApiErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object') {
    const { message, data, response } = err as ApiErrorShape;
    const fromBody = messageFromResponseData(data) ?? messageFromResponseData(response?.data);
    if (fromBody) return fromBody;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return String(err);
};

// ============================================================================
// Permission helpers
// ============================================================================

/**
 * Adapter that exposes a skill's effective permissions in the camelCase shape
 * expected by `@/services/authorization.service` (canManageItem / canWriteItem).
 * The skill payload uses snake_case to mirror the backend wire format.
 */
export const asPermissionedItem = (
  skill: Skill | SkillSummary | null | undefined,
): { effectivePermissions: { read?: boolean; write?: boolean; manage?: boolean } } | null => {
  if (!skill?.effective_permissions) return null;
  return { effectivePermissions: skill.effective_permissions };
};
